import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { Button } from "./ui/button";
import { Telescope, Lock, Sparkles, X, Globe, Loader2, ExternalLink, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { track } from "../lib/analytics";

const PHASES = [
  "Searching real-world case studies…",
  "Mapping the ecosystem…",
  "Analyzing the mindset & cultural realities…",
  "Synthesizing your tailored strategy…",
];

/**
 * DeepResearchDialog — premium-tier-gated.
 * Triggers /api/research/deep-dive (4-part Tavily research + Claude synthesis).
 */
export default function DeepResearchDialog({ open, onClose, onUpgrade }) {
  const [goal, setGoal] = useState("");
  const [phase, setPhase] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setGoal("");
    (async () => {
      try {
        const r = await axios.get(`${API}/billing/me`);
        setBilling(r.data || null);
      } catch (e) {}
    })();
    track("research_dialog_opened");
  }, [open]);

  const sub = billing?.subscription || {};
  const locked = !(sub.is_active && (sub.is_trial || (sub.plan || "").startsWith("advanced")));

  // Cycle through phases while loading
  useEffect(() => {
    if (!loading) return;
    const id = setInterval(() => {
      setPhase((p) => (p + 1) % PHASES.length);
    }, 2200);
    return () => clearInterval(id);
  }, [loading]);

  const submit = async () => {
    if (!goal.trim() || goal.trim().length < 6) {
      toast.error("Tell me your goal in a sentence.");
      return;
    }
    setLoading(true);
    setPhase(0);
    track("research_started", { goal_length: goal.length });
    try {
      const r = await axios.post(`${API}/research/deep-dive`, { goal: goal.trim() });
      setResult(r.data);
      track("research_completed", {
        sources: r.data.sources?.length || 0,
        tavily_configured: r.data.tavily_configured,
      });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        toast.error("Deep Research is included with Advanced.");
        if (onUpgrade) onUpgrade();
        onClose?.();
      } else {
        toast.error(e?.response?.data?.detail || "Research failed. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="research-dialog"
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-white border border-[#eadfc9] shadow-[0_30px_80px_-30px_rgba(15,23,36,0.5)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-[#eadfc9] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#0f1724] flex items-center justify-center">
              <Telescope size={18} className="text-[#c8b88a]" />
            </div>
            <div>
              <p className="font-mono-eyebrow text-[#486a8f]">DEEP RESEARCH · ADVANCED</p>
              <p className="font-display text-[18px] text-[#0f1724] tracking-tight">
                Your personal strategist
              </p>
            </div>
          </div>
          <button
            data-testid="research-dialog-close"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-[#faf6ec] hover:bg-[#f2ecdc] inline-flex items-center justify-center text-[#6b7280]"
          >
            <X size={14} />
          </button>
        </div>

        {/* Locked state */}
        {locked && !result && (
          <div className="p-7 md:p-9 text-center">
            <div className="w-14 h-14 rounded-full bg-[#0f1724] mx-auto mb-5 flex items-center justify-center">
              <Lock size={20} className="text-[#c8b88a]" />
            </div>
            <h3 className="font-display text-[26px] text-[#0f1724] tracking-tight">
              Deep Research is in Advanced.
            </h3>
            <p className="font-body text-[14px] text-[#4b5563] mt-3 max-w-md mx-auto leading-relaxed">
              Your mentor gets full real-time research powers — case studies, ecosystem maps,
              mindset analysis, and a tailored strategy for your specific dream.
              Like having a McKinsey strategist + life mentor in your pocket.
            </p>
            <Button
              data-testid="research-upgrade-btn"
              onClick={() => {
                track("research_upgrade_click");
                if (onUpgrade) onUpgrade();
                else window.location.assign("/pricing");
              }}
              className="mt-7 h-12 px-7 rounded-full bg-[#0f1724] hover:bg-[#1c2944] text-white font-medium"
            >
              <Sparkles size={14} className="mr-2 text-[#c8b88a]" /> Unlock Deep Research
            </Button>
          </div>
        )}

        {/* Input form */}
        {!locked && !result && !loading && (
          <div className="p-7 md:p-9">
            <p className="font-body text-[15px] text-[#0f1724] leading-relaxed mb-5">
              Tell me your specific dream. The more precise, the better the research.
              <span className="block font-body text-[12px] text-[#6b7280] mt-2 italic">
                e.g. "Open a specialty coffee shop in Florence, Italy by 2028" or
                "Become a senior staff engineer at a top US AI lab in 2 years"
              </span>
            </p>
            <textarea
              data-testid="research-goal-input"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              rows={4}
              maxLength={500}
              placeholder="My specific goal is…"
              className="w-full rounded-xl border-2 border-[#e6dcc6] bg-[#faf6ec] p-4 font-body text-[14px] text-[#2b3442] focus:outline-none focus:border-[#486a8f] resize-none"
            />
            <p className="font-body text-[11px] text-[#9aa3ae] mt-1.5">{goal.length}/500</p>
            <Button
              data-testid="research-run-btn"
              onClick={submit}
              disabled={goal.trim().length < 6}
              className="mt-5 w-full h-12 rounded-full bg-[#0f1724] hover:bg-[#1c2944] text-white font-medium text-[15px]"
            >
              <Telescope size={15} className="mr-2 text-[#c8b88a]" /> Run Deep Research
            </Button>
            <p className="font-body text-[11px] text-[#6b7280] text-center mt-3">
              Takes ~30 seconds. Combines real-time web research with my full strategist persona.
            </p>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="p-10 text-center">
            <div className="relative w-24 h-24 mx-auto mb-7">
              <div className="absolute inset-0 rounded-full border-2 border-[#c8b88a] animate-ping opacity-30" />
              <div className="absolute inset-2 rounded-full border-2 border-[#486a8f] animate-pulse opacity-60" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Globe size={32} className="text-[#0f1724] animate-spin-slow" style={{ animation: "spin 4s linear infinite" }} />
              </div>
            </div>
            <p className="font-mono-eyebrow text-[#486a8f] mb-3">RESEARCHING</p>
            <p className="font-display text-[20px] text-[#0f1724] tracking-tight leading-tight max-w-sm mx-auto">
              {PHASES[phase]}
            </p>
            <div className="flex items-center justify-center gap-1.5 mt-6">
              {PHASES.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === phase ? "w-8 bg-[#0f1724]" : i < phase ? "w-2 bg-[#3e6b52]" : "w-2 bg-[#eadfc9]"
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="p-7 md:p-9">
            <div className="mb-5 rounded-2xl bg-[#0f1724] text-white p-5">
              <p className="font-mono-eyebrow text-[#c8b88a] mb-1.5">YOUR GOAL</p>
              <p className="font-body text-[15px] text-white/95 leading-relaxed">{result.goal}</p>
            </div>

            {!result.tavily_configured && (
              <div className="mb-5 rounded-xl bg-[#fbf5e8] border border-[#ead7a8] p-3.5">
                <p className="font-body text-[12px] text-[#7a5a30]">
                  <strong>Note:</strong> Live web search isn't configured yet. This report uses
                  Claude's training knowledge only. Add a Tavily API key to <code>backend/.env</code>{" "}
                  to enable real-time research.
                </p>
              </div>
            )}

            <div
              data-testid="research-report"
              className="prose prose-sm max-w-none font-body text-[14px] text-[#0f1724] leading-[1.7] whitespace-pre-wrap"
            >
              {/* very lightweight markdown render: bold ** ** , ## headings */}
              {renderLightMarkdown(result.report)}
            </div>

            {result.sources && result.sources.length > 0 && (
              <details className="mt-7 rounded-xl bg-[#faf6ec] border border-[#eadfc9] p-4">
                <summary className="font-mono-eyebrow text-[#486a8f] cursor-pointer">
                  {result.sources.length} SOURCES
                </summary>
                <ul className="mt-3 space-y-2">
                  {result.sources.map((s, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="font-mono text-[10px] text-[#9aa3ae] uppercase tracking-wider mt-0.5 w-20 shrink-0">
                        {s.section.replace("_", " ")}
                      </span>
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-body text-[12px] text-[#486a8f] hover:underline inline-flex items-start gap-1 min-w-0"
                      >
                        <span className="truncate">{s.title || s.url}</span>
                        <ExternalLink size={10} className="mt-1 shrink-0" />
                      </a>
                    </li>
                  ))}
                </ul>
              </details>
            )}

            <div className="mt-6 flex flex-wrap gap-2">
              <Button
                data-testid="research-new-btn"
                onClick={() => setResult(null)}
                variant="outline"
                className="rounded-full border-[#d9cfb8] text-[#2b3442]"
              >
                Run another <ArrowRight size={13} className="ml-1.5" />
              </Button>
              <Button
                data-testid="research-copy-btn"
                onClick={() => {
                  navigator.clipboard.writeText(result.report);
                  toast.success("Report copied.");
                  track("research_copied");
                }}
                className="rounded-full bg-[#0f1724] hover:bg-[#1c2944] text-white"
              >
                Copy report
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Lightweight markdown renderer — handles ## headings, ** bold, and bullets
function renderLightMarkdown(md = "") {
  const lines = md.split("\n");
  const out = [];
  let listBuf = [];
  const flushList = (key) => {
    if (listBuf.length === 0) return;
    out.push(
      <ul key={`ul-${key}`} className="list-disc pl-5 space-y-1 my-3">
        {listBuf.map((item, i) => (
          <li key={i} dangerouslySetInnerHTML={{ __html: boldify(item) }} />
        ))}
      </ul>
    );
    listBuf = [];
  };
  lines.forEach((line, i) => {
    if (/^#{1,3}\s/.test(line)) {
      flushList(i);
      const level = (line.match(/^#+/) || [""])[0].length;
      const text = line.replace(/^#+\s*/, "");
      const cls =
        level === 1
          ? "font-display text-[24px] tracking-tight text-[#0f1724] mt-6 mb-2"
          : level === 2
          ? "font-display text-[20px] tracking-tight text-[#0f1724] mt-5 mb-2"
          : "font-display text-[16px] text-[#486a8f] mt-4 mb-1.5";
      out.push(
        <p key={i} className={cls} dangerouslySetInnerHTML={{ __html: boldify(text) }} />
      );
    } else if (/^[-*]\s/.test(line.trim())) {
      listBuf.push(line.trim().replace(/^[-*]\s+/, ""));
    } else if (line.trim() === "") {
      flushList(i);
      out.push(<div key={`sp-${i}`} className="h-2" />);
    } else {
      flushList(i);
      out.push(<p key={i} dangerouslySetInnerHTML={{ __html: boldify(line) }} />);
    }
  });
  flushList("end");
  return out;
}

function boldify(s) {
  return (s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\b(https?:\/\/[^\s)]+)/g, '<a href="$1" target="_blank" class="text-[#486a8f] underline">$1</a>');
}
